import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import OwnerDashboard from "@/pages/OwnerDashboard";
import OperationsDashboard from "@/pages/OperationsDashboard";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Employees from "@/pages/Employees";
import PermissionCenter from "@/pages/PermissionCenter";
import AuditLogs from "@/pages/AuditLogs";
import Appointments from "@/pages/Appointments";
import Laboratory from "@/pages/Laboratory";
import Travel from "@/pages/Travel";
import Transportation from "@/pages/Transportation";
import DriverApp from "@/pages/DriverApp";
import Payments from "@/pages/Payments";
import Messages from "@/pages/Messages";
import Reports from "@/pages/Reports";
import Approvals from "@/pages/Approvals";
import Journey from "@/pages/Journey";
import CashDrawer from "@/pages/CashDrawer";
import ExchangeRates from "@/pages/ExchangeRates";
import Pricing from "@/pages/Pricing";
import Accounting from "@/pages/Accounting";
import Payables from "@/pages/Payables";
import Finance from "@/pages/Finance";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-navy" />
    </div>
  );
}

function Protected({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function ProtectedBare({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequirePerm({ resource, action = "view", children }) {
  const { user, checking, can } = useAuth();
  if (checking || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(resource, action)) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function RequireRole({ roles, perm, children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const ok = (roles && roles.includes(user.role)) || (perm && user.permissions?.[perm]);
  if (!ok) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user?.role === "driver") return <Navigate to="/driver" replace />;
  if (user?.role === "owner" || user?.permissions?.["revenue:view"])
    return <Navigate to="/owner" replace />;
  if (user?.permissions?.["patient_records:view"])
    return <Navigate to="/operations" replace />;
  if (user?.permissions?.["laboratory:view"])
    return <Navigate to="/laboratory" replace />;
  if (user?.permissions?.["transportation:view"])
    return <Navigate to="/transportation" replace />;
  return <Navigate to="/messages" replace />;
}

function LoginRoute() {
  const { user, checking } = useAuth();
  if (checking) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Protected><RootRedirect /></Protected>} />
            <Route path="/owner" element={<RequireRole perm="revenue:view" roles={["owner", "operations_manager", "general_accountant"]}><OwnerDashboard /></RequireRole>} />
            <Route path="/operations" element={<Protected><OperationsDashboard /></Protected>} />
            <Route path="/patients" element={<Protected><Patients /></Protected>} />
            <Route path="/patients/:id" element={<Protected><PatientDetail /></Protected>} />
            <Route path="/appointments" element={<Protected><Appointments /></Protected>} />
            <Route path="/laboratory" element={<Protected><Laboratory /></Protected>} />
            <Route path="/travel" element={<Protected><Travel /></Protected>} />
            <Route path="/transportation" element={<Protected><Transportation /></Protected>} />
            <Route path="/driver" element={<ProtectedBare><DriverApp /></ProtectedBare>} />
            <Route path="/payments" element={<RequirePerm resource="financial_records"><Payments /></RequirePerm>} />
            <Route path="/cash-drawer" element={<RequireRole roles={["translator_cashier", "owner", "operations_manager", "general_accountant"]}><CashDrawer /></RequireRole>} />
            <Route path="/exchange-rates" element={<RequireRole roles={["owner", "operations_manager", "general_accountant"]}><ExchangeRates /></RequireRole>} />
            <Route path="/pricing" element={<RequireRole roles={["owner", "operations_manager", "supervising_dentist", "general_accountant"]}><Pricing /></RequireRole>} />
            <Route path="/accounting" element={<RequireRole roles={["owner", "operations_manager", "supervising_dentist", "general_accountant"]}><Accounting /></RequireRole>} />
            <Route path="/payables" element={<RequireRole roles={["owner", "operations_manager", "supervising_dentist", "general_accountant"]}><Payables /></RequireRole>} />
            <Route path="/finance" element={<RequireRole roles={["owner", "operations_manager", "supervising_dentist", "general_accountant"]}><Finance /></RequireRole>} />
            <Route path="/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/reports" element={<RequirePerm resource="reports"><Reports /></RequirePerm>} />
            <Route path="/approvals" element={<RequirePerm resource="discounts_refunds"><Approvals /></RequirePerm>} />
            <Route path="/journey" element={<Protected><Journey /></Protected>} />
            <Route path="/employees" element={<RequireRole roles={["owner", "operations_manager"]}><Employees /></RequireRole>} />
            <Route path="/permissions" element={<RequireRole roles={["owner"]}><PermissionCenter /></RequireRole>} />
            <Route path="/audit" element={<RequireRole roles={["owner", "operations_manager"]}><AuditLogs /></RequireRole>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
